import { Autocomplete, createFilterOptions, ListItemText, TextField } from '@mui/material';
import { forEach, get, isArray, isEmpty, isEqual } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_WIDGET } from '../constants/constants';
import { FormContext } from '../form/FormContext';
import { useStyling } from '../Theme';
import { AIReferenceFieldStyling } from '../utils/commonUtils';
import { useGoogleMaps } from './GoogleMapsProvider';
import { getLocation, locationLabels, locationToString } from './location';
import LocationQuickAdd from './LocationQuickAdd';
import { getAIAdornment } from './utils/autocompleteUtils';

const valueToOption = (multiple, value) => {
    if (multiple) {
        const opts = [];
        if (isArray(value)) {
            forEach(value, (loc) => {
                const label = loc && loc.label && loc.label.length ? loc.label : locationToString(loc);
                if (label) {
                    opts.push({ label: label, loc: loc });
                }
            });
        }
        return opts;
    } else {
        const label = value && value.label && value.label.length ? value.label : locationToString(value);
        if (label) {
            return { label: label, loc: value };
        }
        return '';
    }
};

export default function LocationsInput(props) {
    const {
        id,
        name,
        inputLabel,
        value,
        onChange,
        required,
        disabled,
        multiple,
        limitTags = 2,
        appendTimeZone = false,
        defaultOptions = [],
        allowAdditionalLocations = true,
        isAIExtractedData = false,
        updateAIDataResolved = undefined,
        hasErrors = false,
        errorText = '',
        size = DEFAULT_WIDGET.defaultWidgetSize,
        variant = DEFAULT_WIDGET.defaultWidgetVariant,
        createLabelUsingAddress = true,
        ...rest
    } = props;
    const [searchText, setSearchText] = useState('');
    const { state, handleChange } = useContext(FormContext);
    const [options, setOptions] = useState([]);
    const [triggerSearch, setTriggerSearch] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [isAIExtractedValue, setIsAIExtractedValue] = useState<boolean>(isAIExtractedData);
    const { aiExtractedFieldColor } = useStyling();
    const { mapsLoaded, placePredictions, getPlacePredictions, isPlacePredictionsLoading, getPlaceDetails } =
        useGoogleMaps();

    useEffect(() => {
        if (value && value.label && value.label !== searchText) {
            setSearchText(value.label);
        }
    }, [value]);

    useEffect(() => {
        if (searchText !== '' && triggerSearch && allowAdditionalLocations) {
            setOptions([]);
            const types = rest.restrictToCountry ? ['country'] : [];
            getPlacePredictions({ input: searchText, includedPrimaryTypes: types });
        }
    }, [searchText]);

    // update local isAIExtractedValue when isAIExtractedData flag changes
    useEffect(() => {
        setIsAIExtractedValue(isAIExtractedData);
    }, [isAIExtractedData]);

    useEffect(() => {
        const labels = locationLabels(multiple ? value : [value]);
        const addNewOptionLabel = `Add "${searchText}"`;
        if (placePredictions.length) {
            const opts = placePredictions
                .filter((place) => {
                    return !labels.includes(place.description);
                })
                .map((place) => {
                    return {
                        key: place.place_id,
                        label: place.description
                    };
                });
            const newOptions = rest.restrictToCountry
                ? opts
                : [{ label: addNewOptionLabel, loc: { label: searchText, primary: false } }, ...opts];
            setOptions(newOptions);
        } else {
            if (!isEmpty(searchText) && !rest.restrictToCountry) {
                setOptions([{ label: addNewOptionLabel, loc: { label: searchText, primary: false } }]);
            }
        }
    }, [placePredictions]);

    const onSelect = (event, option) => {
        if (multiple) {
            const locs = option
                .filter((opt) => {
                    return opt.loc;
                })
                .map((opt) => {
                    return opt.loc;
                });
            option.forEach((o) => {
                if (!o.loc) {
                    getPlaceDetails(o.key).then((placeDetails) => {
                        getLocation(placeDetails, appendTimeZone).then((data) => {
                            let location = data;
                            location.label = o.label;
                            location.primary = locs.length === 0;
                            onChange(name, [...locs, location]);
                        });
                    });
                }
            });
            onChange(name, locs);
        } else {
            if (option && option.loc) {
                setShowAddForm(true);
                onChange(name, option.loc);
            } else {
                if (option) {
                    getPlaceDetails(option.key).then((placeDetails) => {
                        getLocation(placeDetails, appendTimeZone).then((data) => {
                            let location = data;
                            location.label = option.label;
                            location.primary = true;
                            onChange(name, location);
                        });
                    });
                } else {
                    onChange(name, undefined);
                }
            }
        }
        if (isAIExtractedValue) {
            updateAIDataResolved ? updateAIDataResolved(name) : setIsAIExtractedValue(false);
        }
    };

    const filter = createFilterOptions();
    const filterOptions = (options, state) => {
        const results = filter(options, state);
        defaultOptions.map((option) => {
            const isExisting = results.some((existingOption) => {
                return isEqual(existingOption, option);
            });
            if (!isExisting) {
                results.unshift({
                    loc: option,
                    label: option.label
                });
            }
        });

        return results;
    };

    const handleAdd = () => {
        const location = get(state, name);
        const label = locationToString(location, createLabelUsingAddress);
        handleChange(`${name}.label`, label);
        setSearchText(label);
        setShowAddForm(false);
    };

    const handleCancel = () => {
        handleChange(`${name}.label`, '');
        setSearchText('');
        setShowAddForm(false);
    };

    const val = useMemo(() => {
        return valueToOption(multiple, value);
    }, [multiple, value]);

    return (
        <React.Fragment>
            <Autocomplete
                id={id}
                disabled={disabled}
                multiple={multiple}
                fullWidth
                freeSolo
                size={size}
                loading={isPlacePredictionsLoading || !mapsLoaded}
                options={options}
                filterOptions={filterOptions}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option && typeof option.label === 'string') return option.label;
                    return '';
                }}
                renderOption={(props, option, { selected }) => (
                    <li {...props} key={props.id}>
                        <ListItemText primary={option.label} />
                    </li>
                )}
                value={val}
                isOptionEqualToValue={(option, value) => {
                    return option && value && option.label === value.label;
                }}
                renderInput={(inputParams) => (
                    <TextField
                        {...inputParams}
                        size={size}
                        required={required}
                        disabled={disabled}
                        variant={variant}
                        label={inputLabel}
                        error={hasErrors}
                        helperText={errorText}
                        sx={AIReferenceFieldStyling(val, isAIExtractedValue, aiExtractedFieldColor)}
                        InputProps={{
                            ...inputParams.InputProps,
                            size: size,
                            startAdornment: getAIAdornment(inputParams, val, isAIExtractedValue)
                        }}
                    />
                )}
                limitTags={limitTags}
                onChange={onSelect}
                inputValue={searchText}
                onInputChange={(event, value) => {
                    if (event) {
                        setSearchText(value);
                        setTriggerSearch(true);
                    }
                }}
                noOptionsText={'Search for locations'}
            />
            <LocationQuickAdd name={name} isOpen={showAddForm} onCancel={handleCancel} onAdd={handleAdd} />
        </React.Fragment>
    );
}
